// =============================================================
//  audioShared.js -- shared state, utilities, overlay system
//  Used by: audioFile.js, audioPool.js, audioImpulse.js, synth.js and more
// =============================================================

import { registerTask, completeTask } from "../../system/loadTasks.js";
import { isBindable, bindParam } from "../../control/paramBinding.js";
import { evalMaybeRandom, makeValueGenerator, makePatternGenerator, shuffle } from "./patterns.js";
import { patternWord } from "../../parser/cueNames.js";
import { ceilingCurve } from "./ceiling.js";
import { onReset } from "../../system/teardown.js";
import { attachContext, wideStage, copyCompressor } from "./speakers.js";
import { findFile, missNote } from "../../system/mediaFolder.js";
export { evalMaybeRandom, makeValueGenerator, makePatternGenerator };

// =============================================================
//  Shared AudioContext (singleton)
// =============================================================

export const sharedAudioCtx =
  window.sharedAudioCtx ||
  (window.sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)());
attachContext(sharedAudioCtx);   // speakers(): how wide the chain below runs

// =============================================================
//  Master volume node — the single point VOL controls.
//  All per-bus limiters feed here, so VOL is a true master
//  that sits above andrea/barbara/dry/fx/synth buses.
// =============================================================
const _masterVolChain =
  window._oscillaMasterVolChain ||
  (window._oscillaMasterVolChain = (() => {
    const g   = sharedAudioCtx.createGain();
    g.gain.value = 0.85;
    const pan = sharedAudioCtx.createStereoPanner();
    pan.pan.value = 0;
    const lim = sharedAudioCtx.createDynamicsCompressor();
    lim.threshold.value = -2; lim.knee.value = 0;
    lim.ratio.value = 20; lim.attack.value = 0.001; lim.release.value = 0.05;
    // after the limiter, which a wall of noise's attacks can get past: a
    // ceiling nothing goes over (ceiling.js)
    const ceil = sharedAudioCtx.createWaveShaper();
    ceil.curve = ceilingCurve();
    // none: an oversampled shaper's filters ring past the curve on a sharp
    // edge (0.99 out of a 0.95 ceiling, measured); without, it is exact
    ceil.oversample = "none";
    // the very last word: 0 makes a window silent while everything in it
    // still sounds inside -- scopes and analysers read real signal (setSilent)
    const out = sharedAudioCtx.createGain();
    // the pan and the limiter take two channels: with more speakers, the
    // pan moves outputs 1-2 and the limiter holds each pair (speakers.js)
    const panStage = wideStage(sharedAudioCtx, pan);
    const limStage = wideStage(sharedAudioCtx, lim, () => copyCompressor(sharedAudioCtx, lim));
    g.connect(panStage.input);
    panStage.output.connect(limStage.input);
    limStage.output.connect(ceil);
    ceil.connect(out);
    out.connect(sharedAudioCtx.destination);
    return { gain: g, pan, lim, ceil, out };
  })());

/**
 * The whole mix as it is heard -- every bus, after VOL, the limiter and the ceiling,
 * before a silent window's mute: what capture() records (capture.js).
 */
export const masterMix = _masterVolChain.ceil;

/**
 * A silent window: its sounds run, its scopes draw them, nothing reaches the
 * speakers. The live console's projector window (follow mode) is one: the
 * laptop window makes the sound.
 */
export function setSilent(on) {
  const out = _masterVolChain.out;
  if (out) out.gain.value = on ? 0 : 1;
}

const _masterVolNode = _masterVolChain.gain;

// =============================================================
//  Per-bus channel strip
//
//  gain → LP filter → [dry] ──────────────────────┐
//                   → [delaySend] → delay ─────────┤→ limiter → pan → masterVol
//                   → [reverbSend] → conv → reverbWet ┘
//
//  delaySend.gain  = delay mix  (default 0, off)
//  reverbSend.gain = reverb mix (default 0, off)
//  All voices routed to this bus — both dry and wet — so
//  the bus processes everything from that group together.
// =============================================================
function _buildReverbIR(ctx, time = 2.0, damp = 1200) {
  const sr  = ctx.sampleRate;
  const len = Math.max(1, Math.floor(sr * time));
  const ir  = ctx.createBuffer(2, len, sr);
  const a   = 1 - Math.exp(-2 * Math.PI * damp / sr);
  const k   = 6.908 / time;
  for (let ch = 0; ch < 2; ch++) {
    const d = ir.getChannelData(ch);
    let y = 0;
    for (let i = 0; i < len; i++) {
      const env = Math.exp(-k * (i / sr));
      y = a * (Math.random() * 2 - 1) + (1 - a) * y;
      d[i] = y * env;
    }
  }
  return ir;
}

function buildMasterBus(ctx, limiterOpts = {}) {
  const gain = ctx.createGain();
  gain.gain.value = 1.0;

  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 20000;
  filter.Q.value = 0.5;

  // Limiter sums all paths (dry + delay + reverb)
  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = limiterOpts.threshold ?? -3;
  limiter.knee.value      = limiterOpts.knee      ?? 0;
  limiter.ratio.value     = limiterOpts.ratio      ?? 20;
  limiter.attack.value    = limiterOpts.attack     ?? 0.001;
  limiter.release.value   = limiterOpts.release    ?? 0.1;

  const pan = ctx.createStereoPanner();
  pan.pan.value = 0;

  // Delay send (parallel)
  const delaySend = ctx.createGain();
  delaySend.gain.value = 0;  // off by default
  const delayNode = ctx.createDelay(5.0);
  delayNode.delayTime.value = 0.35;
  const delayFb = ctx.createGain();
  delayFb.gain.value = 0.35;
  delayNode.connect(delayFb).connect(delayNode);  // feedback loop
  // two-channel nodes in a chain that may be wider (speakers.js)
  const limStage = wideStage(ctx, limiter, () => copyCompressor(ctx, limiter));
  const panStage = wideStage(ctx, pan);
  filter.connect(delaySend);
  delaySend.connect(delayNode);
  delayNode.connect(limStage.input);

  // Reverb send (parallel)
  const reverbSend = ctx.createGain();
  reverbSend.gain.value = 0;  // off by default
  const conv = ctx.createConvolver();
  conv.normalize = true;
  conv.buffer = _buildReverbIR(ctx);
  filter.connect(reverbSend);
  reverbSend.connect(conv);
  conv.connect(limStage.input);

  // Dry path
  gain.connect(filter);
  filter.connect(limStage.input);
  limStage.output.connect(panStage.input);
  panStage.output.connect(_masterVolNode);

  return { gain, filter, pan, delaySend, delayNode, delayFb, reverbSend, conv };
}

const _dryChain =
  window._oscillaDryChain ||
  (window._oscillaDryChain = buildMasterBus(sharedAudioCtx, {
    threshold: -3, knee: 0, ratio: 20, attack: 0.001, release: 0.1
  }));

// FX bus uses a softer, slower limiter — reverb/delay tails are sustained,
// not transient, so a fast limiter causes pumping rather than protection.
const _fxChain =
  window._oscillaFxChain ||
  (window._oscillaFxChain = buildMasterBus(sharedAudioCtx, {
    threshold: -6, knee: 6, ratio: 8, attack: 0.02, release: 0.4
  }));

// Voices connect to these GainNodes (interface unchanged)
export const masterOutput = _dryChain.gain;
export const fxOutput     = _fxChain.gain;

// All bus effects registries — updated when buses are created/registered
const _busFilterRegistry  = new Map([['dry', _dryChain.filter], ['fx', _fxChain.filter]]);
const _busReverbRegistry  = new Map([['dry', _dryChain.reverbSend], ['fx', _fxChain.reverbSend]]);
const _busDelayRegistry   = new Map([['dry', _dryChain.delaySend], ['fx', _fxChain.delaySend]]);

// Synth bus — separate from audio-file dry bus so synths and samples
// can be balanced independently.
const _synthChain =
  window._oscillaSynthChain ||
  (window._oscillaSynthChain = buildMasterBus(sharedAudioCtx, {
    threshold: -3, knee: 3, ratio: 12, attack: 0.005, release: 0.2
  }));

export const synthOutput = _synthChain.gain;

// Register synth chain in effect registries
_busFilterRegistry.set('synth', _synthChain.filter);
_busReverbRegistry.set('synth', _synthChain.reverbSend);
_busDelayRegistry.set('synth',  _synthChain.delaySend);

// =============================================================
//  Named bus registry
//  Scores / extensions can create custom output buses and assign
//  audio objects to them via outputBus: "busName" in playback.
// =============================================================
const _busRegistry = new Map([
  ['dry',   masterOutput],
  ['fx',    fxOutput],
  ['synth', synthOutput],
]);

export function registerBus(name, limiterOpts = {}) {
  if (_busRegistry.has(name)) return _busRegistry.get(name);
  const chain = buildMasterBus(sharedAudioCtx, {
    threshold: -3, knee: 0, ratio: 20, attack: 0.001, release: 0.1,
    ...limiterOpts
  });
  _busRegistry.set(name,       chain.gain);
  _busPanRegistry.set(name,    chain.pan);
  _busFilterRegistry.set(name, chain.filter);
  _busReverbRegistry.set(name, chain.reverbSend);
  _busDelayRegistry.set(name,  chain.delaySend);
  return chain.gain;
}

export function getBus(name) {
  if (!name) return masterOutput;
  return _busRegistry.get(name) ?? masterOutput;
}

// =============================================================
//  Master gain signal binding
//  Call connectMasterGain(faderUid, min, max) to subscribe any
//  o2p/controlXY fader's .t channel to the master gain.
//  e.g. from a score extension: connectMasterGain('masterVol', 0, 1)
// =============================================================
let _masterGainUnsub = null;
let _fxGainUnsub = null;

function _bindBusGain(node, faderUid, min, max) {
  return import('../../control/paramBus.js').then(({ subscribe }) => {
    return subscribe(`${faderUid}.t`, (val) => {
      // Square-law (exp2) taper: feels logarithmic to the ear.
      // At fader midpoint (0.5): gain = 0.25 (–12 dB), not 0.5 (–6 dB).
      const mapped = min + (val * val) * (max - min);
      node.gain.setTargetAtTime(mapped, sharedAudioCtx.currentTime, 0.02);
    });
  });
}

export function connectMasterGain(faderUid, min = 0, max = 1) {
  if (_masterGainUnsub) _masterGainUnsub();
  // VOL controls _masterVolNode — the true master above all buses — not a
  // per-voice postFader.
  window._oscillaMasterVolPath = null;  // disable postFader subscription
  _bindBusGain(_masterVolNode, faderUid, min, max).then(u => { _masterGainUnsub = u; });
}

export function connectFxGain(faderUid, min = 0, max = 1) {
  if (_fxGainUnsub) _fxGainUnsub();
  _bindBusGain(fxOutput, faderUid, min, max).then(u => { _fxGainUnsub = u; });
}

let _synthGainUnsub = null;
export function connectSynthGain(faderUid, min = 0, max = 1) {
  if (_synthGainUnsub) _synthGainUnsub();
  _bindBusGain(synthOutput, faderUid, min, max).then(u => { _synthGainUnsub = u; });
}
window._oscillaConnectSynthGain = connectSynthGain;

window._oscillaConnectMasterGain = connectMasterGain;
window._oscillaConnectFxGain    = connectFxGain;

// Generic: connect any named bus to a fader signal
const _busGainUnsubs = new Map();
export function connectBusGain(busName, faderUid, min = 0, max = 1) {
  const prev = _busGainUnsubs.get(busName);
  if (prev) prev();
  const node = getBus(busName);
  _bindBusGain(node, faderUid, min, max).then(u => {
    _busGainUnsubs.set(busName, u);
  });
}
window._oscillaConnectBusGain = connectBusGain;
window._oscillaRegisterBus    = registerBus;
export function _oscillaGetBusNames() {
  return Array.from(_busRegistry.keys());
}

// =============================================================
//  Direct mixer API — for HTML mixer overlay (bypasses ParamBus)
// =============================================================
window._mixerAPI = {
  get(bus) {
    const gain   = getBus(bus);
    const pan    = _busPanRegistry.get(bus);
    const filter = _busFilterRegistry.get(bus);
    const rev    = _busReverbRegistry.get(bus);
    const dly    = _busDelayRegistry.get(bus);
    return {
      gain:   gain   ? Math.sqrt(Math.max(0, gain.gain.value))              : 0.85,
      pan:    pan    ? pan.pan.value                                         : 0,
      freq:   filter ? Math.log(filter.frequency.value / 20) / Math.log(1000) : 1,
      q:      filter ? (filter.Q.value - 0.5) / 11.5                        : 0,
      reverb: rev    ? Math.sqrt(Math.max(0, rev.gain.value / 0.6))         : 0,
      delay:  dly    ? Math.sqrt(Math.max(0, dly.gain.value / 0.6))         : 0,
    };
  },
  getMaster() {
    return {
      gain: Math.sqrt(Math.max(0, _masterVolChain.gain.gain.value)),
      pan:  _masterVolChain.pan.pan.value,
    };
  },
  setGain(bus, t)   { const n=getBus(bus); if(!n) return; const v=t*t; n.gain.cancelScheduledValues(sharedAudioCtx.currentTime); n.gain.setTargetAtTime(v, sharedAudioCtx.currentTime, 0.02); },
  setPan(bus, v)    { const n=_busPanRegistry.get(bus); if(!n) return; n.pan.setTargetAtTime(v, sharedAudioCtx.currentTime, 0.02); },
  setFreq(bus, t)   { const f=_busFilterRegistry.get(bus); if(!f) return; f.frequency.setTargetAtTime(20*Math.pow(1000,t), sharedAudioCtx.currentTime, 0.02); },
  setQ(bus, t)      { const f=_busFilterRegistry.get(bus); if(!f) return; f.Q.setTargetAtTime(0.5+t*11.5, sharedAudioCtx.currentTime, 0.02); },
  setReverb(bus, t) { const n=_busReverbRegistry.get(bus); if(!n) return; n.gain.setTargetAtTime(t*t*0.6, sharedAudioCtx.currentTime, 0.02); },
  setDelay(bus, t)  { const n=_busDelayRegistry.get(bus); if(!n) return; n.gain.setTargetAtTime(t*t*0.6, sharedAudioCtx.currentTime, 0.02); },
  setMasterGain(t)  { _masterVolNode.gain.cancelScheduledValues(sharedAudioCtx.currentTime); _masterVolNode.gain.setTargetAtTime(t*t, sharedAudioCtx.currentTime, 0.02); },
  setMasterPan(v)   { _masterVolChain.pan.pan.setTargetAtTime(v, sharedAudioCtx.currentTime, 0.02); },
};

// Pan registry: busName → panNode
const _busPanRegistry = new Map([
  ['dry',   _dryChain.pan],
  ['fx',    _fxChain.pan],
  ['synth', _synthChain.pan],
  ['vol',   _masterVolChain.pan],
]);

const _busPanUnsubs = new Map();

export function connectBusPan(busName, faderUid) {
  const prev = _busPanUnsubs.get(busName);
  if (prev) prev();
  const panNode = _busPanRegistry.get(busName);
  if (!panNode) { console.warn(`[masterBus] no pan node for bus: ${busName}`); return; }
  import('../../control/paramBus.js').then(({ subscribe }) => {
    const unsub = subscribe(`${faderUid}.t`, (val) => {
      // t=0 → -1 (full left), t=0.5 → 0 (centre), t=1 → +1 (full right)
      panNode.pan.setTargetAtTime((val - 0.5) * 2, sharedAudioCtx.currentTime, 0.02);
    });
    _busPanUnsubs.set(busName, unsub);
  });
}
window._oscillaConnectBusPan = connectBusPan;

// =============================================================
//  Master LP filter binding
//  connectMasterFilter(freqUid, qUid)
//  freqUid.t → frequency (20 Hz – 20 kHz, logarithmic)
//  qUid.t    → Q / resonance (0.5 – 12)
// =============================================================
let _filterUnsubs = [];

export function connectMasterFilter(freqUid, qUid) {
  _filterUnsubs.forEach(u => u());
  _filterUnsubs = [];
  import('../../control/paramBus.js').then(({ subscribe }) => {
    const setFreq = (val) => {
      const hz = 20 * Math.pow(1000, val);
      for (const f of _busFilterRegistry.values())
        f.frequency.setTargetAtTime(hz, sharedAudioCtx.currentTime, 0.02);
    };
    const setQ = (val) => {
      const q = 0.5 + val * 11.5;
      for (const f of _busFilterRegistry.values())
        f.Q.setTargetAtTime(q, sharedAudioCtx.currentTime, 0.02);
    };
    _filterUnsubs.push(subscribe(`${freqUid}.t`, setFreq));
    _filterUnsubs.push(subscribe(`${qUid}.t`,   setQ));
  });
}
window._oscillaConnectMasterFilter = connectMasterFilter;

// Per-bus LP filter binding (independent per bus)
const _busFilterUnsubs = new Map();
export function connectBusFilter(busName, freqFaderUid, qFaderUid) {
  const prev = _busFilterUnsubs.get(busName);
  if (prev) { prev.freqU(); prev.qU(); }
  const filter = _busFilterRegistry.get(busName);
  if (!filter) return;
  import('../../control/paramBus.js').then(({ subscribe }) => {
    const freqU = subscribe(`${freqFaderUid}.t`, (val) => {
      filter.frequency.setTargetAtTime(20 * Math.pow(1000, val), sharedAudioCtx.currentTime, 0.02);
    });
    const qU = subscribe(`${qFaderUid}.t`, (val) => {
      filter.Q.setTargetAtTime(0.5 + val * 11.5, sharedAudioCtx.currentTime, 0.02);
    });
    _busFilterUnsubs.set(busName, { freqU, qU });
  });
}
window._oscillaConnectBusFilter = connectBusFilter;

// Per-bus reverb and delay mix binding
const _busReverbUnsubs = new Map();
const _busDelayUnsubs  = new Map();

export function connectBusReverb(busName, faderUid, min = 0, max = 0.6) {
  const prev = _busReverbUnsubs.get(busName);
  if (prev) prev();
  const node = _busReverbRegistry.get(busName);
  if (!node) return;
  import('../../control/paramBus.js').then(({ subscribe }) => {
    const u = subscribe(`${faderUid}.t`, (val) => {
      node.gain.setTargetAtTime(min + (val * val) * (max - min), sharedAudioCtx.currentTime, 0.02);
    });
    _busReverbUnsubs.set(busName, u);
  });
}

export function connectBusDelay(busName, faderUid, min = 0, max = 0.6) {
  const prev = _busDelayUnsubs.get(busName);
  if (prev) prev();
  const node = _busDelayRegistry.get(busName);
  if (!node) return;
  import('../../control/paramBus.js').then(({ subscribe }) => {
    const u = subscribe(`${faderUid}.t`, (val) => {
      node.gain.setTargetAtTime(min + (val * val) * (max - min), sharedAudioCtx.currentTime, 0.02);
    });
    _busDelayUnsubs.set(busName, u);
  });
}

window._oscillaConnectBusReverb = connectBusReverb;
window._oscillaConnectBusDelay  = connectBusDelay;

// Unlock AudioContext on user interaction (browser autoplay policy).
// Safari on iPad can be especially strict — resume() may resolve but
// the context stays effectively muted until a stronger gesture occurs.
// We keep retrying on every interaction until the state is "running",
// and play a silent buffer to force Safari to fully commit.
function unlockAudioContext() {
  if (sharedAudioCtx.state === "running") {
    // Already unlocked — remove all listeners
    document.removeEventListener("click", unlockAudioContext);
    document.removeEventListener("touchstart", unlockAudioContext);
    document.removeEventListener("touchend", unlockAudioContext);
    document.removeEventListener("keydown", unlockAudioContext);
    return;
  }

  sharedAudioCtx.resume().then(() => {
    // Play a silent buffer to force Safari to fully activate the context.
    // Some Safari versions need actual audio output during a user gesture.
    try {
      const silentBuf = sharedAudioCtx.createBuffer(1, 1, sharedAudioCtx.sampleRate);
      const src = sharedAudioCtx.createBufferSource();
      src.buffer = silentBuf;
      src.connect(sharedAudioCtx.destination);
      src.start(0);
    } catch (e) { /* ignore */ }

    if (sharedAudioCtx.state === "running") {
      console.log("[audio] AudioContext unlocked");
      document.removeEventListener("click", unlockAudioContext);
      document.removeEventListener("touchstart", unlockAudioContext);
      document.removeEventListener("touchend", unlockAudioContext);
      document.removeEventListener("keydown", unlockAudioContext);
    }
  }).catch(() => { /* resume refused without a user gesture */ });
}

document.addEventListener("click", unlockAudioContext);
document.addEventListener("touchstart", unlockAudioContext);
document.addEventListener("touchend", unlockAudioContext);
document.addEventListener("keydown", unlockAudioContext);

// =============================================================
//  Silent keepalive: a tab "playing audio" keeps its timers unthrottled
//  when hidden (requestAnimationFrame still stops)
// =============================================================
let _keepaliveStarted = false;

function startSilentKeepalive() {
  if (_keepaliveStarted) return;
  if (sharedAudioCtx.state === "suspended") return; // Wait for unlock

  try {
    const osc = sharedAudioCtx.createOscillator();
    const gain = sharedAudioCtx.createGain();
    gain.gain.value = 0; // Silent
    osc.connect(gain);
    gain.connect(sharedAudioCtx.destination);
    osc.start();
    _keepaliveStarted = true;
  } catch (e) {
    console.warn("[audio] Failed to start keepalive:", e);
  }
}

// Start keepalive when audio context is unlocked
sharedAudioCtx.addEventListener?.("statechange", () => {
  if (sharedAudioCtx.state === "running") {
    startSilentKeepalive();
  }
});

// Also try on first user interaction (after unlock)
document.addEventListener("click", startSilentKeepalive, { once: true });

// =============================================================
//  Audio file extensions
// =============================================================

const AUDIO_EXTENSIONS = [".wav", ".mp3", ".ogg", ".webm", ".aac", ".flac", ".aiff", ".aif", ".m4a"];

/**
 * Check if a path has a recognized audio extension
 */
export function hasAudioExtension(path) {
  if (!path) return false;
  const lower = path.toLowerCase();
  return AUDIO_EXTENSIONS.some(ext => lower.endsWith(ext));
}

/**
 * Ensure a path has an audio extension (default to .wav if none)
 */
export function ensureAudioExtension(path) {
  if (!path) return path;
  return hasAudioExtension(path) ? path : `${path}.wav`;
}

// =============================================================
//  Shared state
// =============================================================

export const audioBufferCache = window.audioBufferCache || new Map();
window.audioBufferCache = audioBufferCache;

export const audioLastHit = window.audioLastHit || new Map();
window.audioLastHit = audioLastHit;

export { activeAudioCues, activeSynthVoices } from "./voices.js";

// =============================================================
//  Bidirectional alternation
//  Each audio object that has bidirectional ticked alternates direction
//  per trigger (forward, reverse, forward, ...). Keyed by uid so each
//  audio object has its own toggle state. Returns the signed speed.
// =============================================================
const _bidirToggle = new Map();
export function nextBidirectionalSpeed(uid, baseSpeed) {
  const flip = _bidirToggle.get(uid) || false;
  _bidirToggle.set(uid, !flip);
  return flip ? -baseSpeed : baseSpeed;
}

// =============================================================
//  Utility: evaluate random expressions from DSL
// =============================================================

/** Format a number to at most 2 decimal places, strip trailing zeros. */
export function fmtNum(v) {
  // a follow()/map(): what it follows, not [object Object]
  if (isBindable(v)) return v.type === "signalRef" ? `${v.source}.${v.channel}` : "follow";
  const n = Number(v);
  return Number.isFinite(n) ? parseFloat(n.toFixed(2)) : v;
}

// =============================================================
//  Fade resolution
//
//  Handles percentage strings ("50%"), rand/irand funcCalls with
//  percentage args, parser wrapper objects, and plain numbers.
//  effectiveDuration is the playback-speed-adjusted file length,
//  used to resolve percentage values.
// =============================================================

export function resolveFade(v, effectiveDuration, fallback = 0) {
  if (v == null) return fallback;

  // funcCall with percentage or numeric args: rand("10%","50%") etc.
  if (typeof v === "object" && v.type === "funcCall"
      && (v.name === "rand" || v.name === "irand")) {
    const [argA, argB] = v.args || [];

    const resolveArg = (arg) => {
      if (typeof arg === "string" && arg.trim().endsWith("%")) {
        const pct = Number(arg.replace("%", "").trim()) / 100;
        return isNaN(pct) ? 0 : effectiveDuration * pct;
      }
      return Number(arg) || 0;
    };

    const minVal = resolveArg(argA);
    const maxVal = resolveArg(argB);
    const result = Math.min(minVal, maxVal)
                 + Math.random() * Math.abs(maxVal - minVal);
    return v.name === "irand" ? Math.floor(result) : result;
  }

  // Percentage string: "50%"
  if (typeof v === "string" && v.trim().endsWith("%")) {
    const pct = Number(v.replace("%", "").trim()) / 100;
    return isNaN(pct) ? fallback : effectiveDuration * pct;
  }

  // Parser wrapper: { value: ... }
  if (typeof v === "object" && v.value !== undefined) {
    return resolveFade(v.value, effectiveDuration, fallback);
  }

  // Plain number (seconds)
  const num = Number(v);
  return isNaN(num) ? fallback : num;
}

// =============================================================
//  Info label builder
//
//  Builds the "filename | amp:0.5 | loop:inf | ..." string used
//  in waveform text overlays.  Only includes params that differ
//  from defaults.  Pass any subset of keys.
// =============================================================

/**
 * Read playbackClients array from a cue element's dataset.
 */
export function getPlaybackClientsFromEl(el) {
  if (!el?.dataset?.playbackClients) return undefined;
  try { return JSON.parse(el.dataset.playbackClients); } catch { return undefined; }
}

/**
 * Build the header part of the audio info label (type + label + filename).
 * This is always shown even when details are hidden.
 */
export function buildAudioInfoHeader({ audioObjectType, audioObjectLabel, filename, playbackClients } = {}) {
  if (!audioObjectType) return "";
  let header = audioObjectType;
  if (audioObjectLabel) {
    header += ` [${audioObjectLabel.toUpperCase()}]`;
  }
  if (playbackClients?.length) {
    header += ` → ${playbackClients.join(", ")}`;
  }
  if (filename) {
    // Extract just the filename from path
    const basename = filename.split("/").pop() || filename;
    header += ` - ${basename}`;
  }
  return header;
}

/**
 * Build the details part of the audio info label (filename + parameters).
 * This can be toggled on/off by the user.
 */
export function buildAudioInfoDetails({
  filename, amp, loop, speed, pan,
  inPt, outPt, fadeIn, fadeOut, poly
} = {}) {
  const parts = [];
  if (filename) parts.push(filename);
  if (amp != null && Number(amp) !== 1) parts.push(`amp:${fmtNum(amp)}`);
  if (loop != null && loop !== 1) {
    parts.push(`loop:${loop === 0 || loop === Infinity ? "inf" : loop}`);
  }
  if (speed != null && Number(speed) !== 1) parts.push(`speed:${fmtNum(speed)}`);
  if (pan != null && Number(pan) !== 0) parts.push(`pan:${fmtNum(pan)}`);
  if (Number(inPt) > 0) parts.push(`in:${fmtNum(inPt)}s`);
  if (Number(outPt) > 0) parts.push(`out:${fmtNum(outPt)}s`);
  if (Number(fadeIn) > 0) parts.push(`fade-in:${fmtNum(fadeIn)}s`);
  if (Number(fadeOut) > 0) parts.push(`fade-out:${fmtNum(fadeOut)}s`);
  if (poly != null) parts.push(`poly:${poly === 0 ? "inf" : poly}`);
  return parts.join(" | ");
}

/**
 * Build the full audio info label (header + details combined).
 * Kept for backwards compatibility.
 */
export function buildAudioInfoLabel({
  filename, amp, loop, speed, pan,
  inPt, outPt, fadeIn, fadeOut, poly,
  audioObjectType, audioObjectLabel
} = {}) {
  const header = buildAudioInfoHeader({ audioObjectType, audioObjectLabel, filename });
  const details = buildAudioInfoDetails({
    amp, loop, speed, pan,
    inPt, outPt, fadeIn, fadeOut, poly
  });

  if (header && details) return `${header} | ${details}`;
  return header || details || "";
}

// =============================================================
//  Parameters that follow a signal
//  audio(amp: follow(f1.t, 0, 0.8)), audioPool(speed: map(adc.pitch,
//  200, 1000, 0.5, 2)), audioImpulse(rate: follow(adc.amp, 10, 120)):
//  the sound starts from the signal's value now and is steered as it
//  moves -- a voice through its own update(), a process through its
//  settings for the next tick.
// =============================================================

/**
 * Of `values` (name -> value as written), those that are follow()/map():
 * { name: { ref, now } }, `now` the value they have at this moment.
 */
export function takeFollows(values) {
  const out = {};
  for (const [k, v] of Object.entries(values)) {
    if (!isBindable(v)) continue;
    const b = bindParam(v, () => {});
    b.unbind?.();
    out[k] = { ref: v, now: b.value };
  }
  return out;
}

/** A value as it is now: a follow()/map() read off its source, rand() rolled. */
export function valueNow(v) {
  return isBindable(v) ? takeFollows({ v }).v.now : evalMaybeRandom(v);
}

/** From now on, apply({ name: value }) as each source moves. Returns the release. */
export function steerFollows(follows, apply) {
  const unbinds = Object.entries(follows || {}).map(([k, f]) =>
    bindParam(f.ref, v => { if (Number.isFinite(v)) apply({ [k]: v }); }).unbind);
  return () => { for (const u of unbinds) { try { u?.(); } catch { /* already gone */ } } };
}

// Does this parsed value draw from Math.random when evaluated? Covers the
// same shapes evalMaybeRandom accepts, plus patterns: Prand/Pxrand/Pshuf/
// Pchoose draw locally, and a deterministic Pseq is random if any of its
// entries is. Per-client randomness is a feature (performers diverge on
// their own dice while sharing the transport), so animations driven by it
// default to sync:0 — see the animation cues' cfg builders.
export function isRandomised(v) {
  if (v == null) return false;
  if (typeof v === "string") {
    return /^\s*['"]?\s*(rand|irand|birand|hrand)\s*\(/.test(v);
  }
  if (Array.isArray(v)) return v.some(isRandomised);
  if (typeof v !== "object") return false;
  if (v.type === "rand" || v.type === "irand" || v.type === "birand") return true;
  if (v.type === "funcCall" || v.type === "func") {
    if (["rand", "irand", "birand", "hrand", "rnd"].includes(v.name)) return true;
    return Array.isArray(v.args) && v.args.some(isRandomised);
  }
  // Animation patterns arrive as {type:"pattern", name:"Prand", values}; page
  // patterns as {type:"Prand", list}. Read the name from either.
  if (v.type === "pattern" || /^P[a-z]+$/.test(String(v.type || "")) || /^P[a-z]+$/.test(String(v.name || ""))) {
    const name = String(v.name || (/^P[a-z]+$/.test(String(v.type || "")) ? v.type : "Pseq"));
    if (["Prand", "Pxrand", "Pshuf", "Pchoose", "Pwrand", "Pbrown", "Pgauss", "Plprand", "Phprand", "Pmeanrand", "Pbeta", "Pcauchy", "Ppoisson"].includes(name)) return true;
    return Array.isArray(v.values) && v.values.some(isRandomised);
  }
  if (Array.isArray(v.list)) return v.list.some(isRandomised);
  return false;
}


// =============================================================
//  Utility: select file from pool
//
//  Shared selection logic for audioPool and audioImpulse.
//  Modes:
//    "rand"      -- pure random, repeats possible
//    "shuffle"   -- no repeats until pool exhausted, then reshuffle
//    "sequential"-- play in order, wrap around
// =============================================================

/**
 * Select the next file from a pool object.
 * Mutates pool.cursor and pool.files (on reshuffle).
 *
 * @param {object} pool - { files: string[], mode: string, cursor: number }
 * @returns {string|null} selected filename, or null if pool empty
 */
export function selectFromPool(pool) {
  if (!pool?.files?.length) return null;

  if (pool.mode === "rand") {
    return pool.files[Math.floor(Math.random() * pool.files.length)];
  }

  // sequential or shuffle: use cursor
  const file = pool.files[pool.cursor % pool.files.length];
  pool.cursor++;

  // shuffle: reshuffle when exhausted
  if (pool.mode !== "sequential" && pool.cursor >= pool.files.length) {
    pool.files = shuffle(pool.files);
    pool.cursor = 0;
  }

  // sequential: wrap around
  if (pool.mode === "sequential" && pool.cursor >= pool.files.length) {
    pool.cursor = 0;
  }

  return file;
}

// =============================================================
//  Utility: generate fallback sine buffer (for offline testing)
// =============================================================

export function generateToneBuffer(ctx, freq = 440, dur = 0.3, amp = 0.3) {
  const rate = ctx.sampleRate;
  const len = rate * dur;
  const buf = ctx.createBuffer(1, len, rate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.sin((2 * Math.PI * freq * i) / rate) * amp;
  return buf;
}

// =============================================================
//  Buffer reversal for negative speed playback
//  Shared by audioFile, audioPool, audioImpulse
// =============================================================

const reversedBufferCache = new Map();

/**
 * Get (or create + cache) a sample-reversed copy of an AudioBuffer.
 * Used for reverse playback since Web Audio API does not support
 * negative playbackRate.
 *
 * @param {AudioContext} ctx
 * @param {AudioBuffer} buf - original buffer
 * @param {string} cacheKey - typically `${filename}__rev`
 * @returns {AudioBuffer} reversed copy
 */
export function getReversedBuffer(ctx, buf, cacheKey) {
  if (reversedBufferCache.has(cacheKey)) return reversedBufferCache.get(cacheKey);

  const reversed = ctx.createBuffer(buf.numberOfChannels, buf.length, buf.sampleRate);
  for (let ch = 0; ch < buf.numberOfChannels; ch++) {
    const src = buf.getChannelData(ch);
    const dst = reversed.getChannelData(ch);
    for (let i = 0, j = src.length - 1; i < src.length; i++, j--) {
      dst[i] = src[j];
    }
  }

  reversedBufferCache.set(cacheKey, reversed);
  return reversed;
}

// =============================================================
//  Fetch + decode audio buffer with caching
//
//  Resolution order:
//    0. a folder picked on this computer (mediaFolder.js)  -- if loaded
//    1. project audio dir   (scores/<project>/audio/<filename>)
//    2. shared audio dir    (shared/audio/<filename>)
//
//  Caches decoded buffers in audioBufferCache by filename.
//  Also caches in-flight promises to prevent duplicate fetches.
// =============================================================

// Cache for in-flight fetch promises (prevents duplicate network requests)
const fetchPromiseCache = new Map();

// another folder picked: the same name may be another sound now
window.addEventListener("oscilla:media-folder", () => { audioBufferCache.clear(); reversedBufferCache.clear(); });

export async function fetchAudioBuffer(ctx, filename) {
  // 1. Return cached buffer if available
  let buf = audioBufferCache.get(filename);
  if (buf) return buf;

  // 2. Return existing in-flight promise if this file is already being fetched
  if (fetchPromiseCache.has(filename)) {
    return fetchPromiseCache.get(filename);
  }

  // Register with progress indicator (if not already registered by primeWaveform)
  const taskId = `audio:${filename}`;
  if (!window._audioTaskRegistered?.has(taskId)) {
    registerTask(taskId, filename);
  }

  // 3. Create new fetch promise and cache it
  const fetchPromise = (async () => {
    const local = filename.startsWith("/tts/") ? null : findFile(filename, "audio");
    if (local) {
      const decodedBuf = await ctx.decodeAudioData(await local.arrayBuffer());
      audioBufferCache.set(filename, decodedBuf);
      return decodedBuf;
    }
    const projectPath = (window.resolveProjectPath || resolveProjectPath)("audio", filename);
    const sharedPath = `${window.sharedDir || "shared/"}audio/${filename}`;

    // Try paths in order - direct GET, no HEAD checks
    // This halves the number of requests
    let resp;

    // 0. speech made by the server (say(), server/tts.js): its own url
    if (filename.startsWith("/tts/")) resp = await fetch(filename);
    // 1. Try project path (none on a static host: Live there has no project), 2. then the shared one
    else {
      if (window.audioDir) resp = await fetch(projectPath);
      if (!resp?.ok) resp = await fetch(sharedPath);
    }

    if (!resp.ok) {
      throw new Error(`[audio] Failed to fetch ${filename}: ${resp.status}${missNote(filename, "audio")}`);
    }

    const decodedBuf = await ctx.decodeAudioData(await resp.arrayBuffer());
    audioBufferCache.set(filename, decodedBuf);
    return decodedBuf;
  })();

  fetchPromiseCache.set(filename, fetchPromise);

  try {
    const result = await fetchPromise;
    return result;
  } finally {
    // Clean up promise cache after completion (success or failure)
    fetchPromiseCache.delete(filename);
    // Mark task complete in progress indicator
    completeTask(taskId);
    // Clean up task registration tracking
    window._audioTaskRegistered?.delete(taskId);
  }
}

// local ref for when window.resolveProjectPath isn't set yet
function resolveProjectPath(type, filename) {
  if (type === "audio") return `${window.audioDir || "audio/"}${filename}`;
  return filename;
}

// =============================================================
//  AUDIO OVERLAY SYSTEM
// =============================================================

/**
 * Parse overlay flag: 0/off, 1/brief, 2/expanded (default)
 */
export function parseOverlayLevel(value) {
  if (value === undefined || value === null) return 2;

  const strVal = String(value).toLowerCase().trim();
  if (strVal === "off" || strVal === "false" || strVal === "none" || strVal === "0") return 0;
  if (strVal === "brief" || strVal === "short" || strVal === "min" || strVal === "1") return 1;
  if (strVal === "expanded" || strVal === "full" || strVal === "true" || strVal === "2") return 2;

  const numVal = Number(value);
  if (!isNaN(numVal)) {
    if (numVal <= 0) return 0;
    if (numVal === 1) return 1;
    return 2;
  }
  return 2;
}

/**
 * Format a pattern's repeat count for display.
 * The parser pops a trailing bare number/inf out of the values list into
 * `repeats` — without this suffix the overlay looks truncated (the "last
 * value" of the written DSL disappears).
 */
export function formatPatternRepeats(repeats) {
  if (repeats == null) return "";
  return `×${repeats === Infinity ? "inf" : repeats}`;
}

/**
 * Format a value for overlay display - handles funcCall, rand, patterns
 */
export function formatOverlayValue(v) {
  if (v === null || v === undefined) return "?";

  // Chord array within a pattern: [a, b, c]
  if (Array.isArray(v)) {
    return `[${v.map(formatOverlayValue).join(",")}]`;
  }

  if (typeof v === "object") {
    if (v.type === "funcCall" && v.name) {
      const args = Array.isArray(v.args) ? v.args.join(",") : "";
      return `${v.name}(${args})`;
    }
    if (v.type === "rand" || v.type === "irand") {
      return `${v.type}(${v.min},${v.max})`;
    }
    if (v.type === "pattern" && v.name) {
      const vals = Array.isArray(v.values) ? v.values.map(formatOverlayValue).join(",") : "";
      return `${patternWord(v.name)}[${vals}]${formatPatternRepeats(v.repeats)}`;
    }
    return JSON.stringify(v);
  }

  if (typeof v === "number") {
    if (Number.isInteger(v)) return String(v);
    return v.toFixed(2).replace(/\.?0+$/, "");
  }

  return String(v);
}

/**
 * Format overlay text for audioFile
 */
export function formatAudioFileOverlay(params, level) {
  const parts = ["audioFile"];
  const src = params.src || params.file || "?";
  parts.push(src);

  if (level >= 2) {
    if (params.amp !== undefined && params.amp !== 1) parts.push(`amp:${formatOverlayValue(params.amp)}`);
    if (params.loop !== undefined && params.loop !== 1) parts.push(`loop:${params.loop}`);
    const speedOv = params.speed ?? params.pitch;
    if (speedOv !== undefined && speedOv !== 1) parts.push(`speed:${formatOverlayValue(speedOv)}`);
    if (params.pan !== undefined && params.pan !== 0) parts.push(`pan:${formatOverlayValue(params.pan)}`);
    if (params.fadein || params.fadeIn) parts.push(`fadeIn:${formatOverlayValue(params.fadein || params.fadeIn)}`);
    if (params.fadeout || params.fadeOut) parts.push(`fadeOut:${formatOverlayValue(params.fadeout || params.fadeOut)}`);
    if (params.fade) parts.push(`fade:${formatOverlayValue(params.fade)}`);
  }

  return parts.join(" | ");
}

/**
 * Format overlay text for audioPool
 */
export function formatAudioPoolOverlay(params, level) {
  const parts = ["audioPool"];
  parts.push(params.path || "?");

  if (level >= 2) {
    if (params.uid) parts.push(`uid:${params.uid}`);
    if (params.mode && params.mode !== "shuffle") parts.push(`mode:${params.mode}`);
    if (params.amp !== undefined) parts.push(`amp:${formatOverlayValue(params.amp)}`);
    if (params.pan !== undefined) parts.push(`pan:${formatOverlayValue(params.pan)}`);
    if ((params.speed ?? params.pitch) !== undefined) parts.push(`speed:${formatOverlayValue(params.speed ?? params.pitch)}`);
    if (params.poly !== undefined && params.poly !== 1) parts.push(`poly:${params.poly}`);
    if (params.fadeout || params.fadeOut) parts.push(`fadeOut:${formatOverlayValue(params.fadeout || params.fadeOut)}`);
  }

  return parts.join(" | ");
}

/**
 * Format overlay text for audioImpulse
 */
export function formatAudioImpulseOverlay(params, level) {
  const parts = ["audioImpulse"];
  parts.push(params.path || "?");

  if (level >= 2) {
    if (params.rate !== undefined) parts.push(`rate:${params.rate}`);
    if (params.jitter !== undefined && params.jitter !== 0) parts.push(`jitter:${formatOverlayValue(params.jitter)}`);
    if (params.amp !== undefined) parts.push(`amp:${formatOverlayValue(params.amp)}`);
    if (params.pan !== undefined) parts.push(`pan:${formatOverlayValue(params.pan)}`);
    if ((params.speed ?? params.pitch) !== undefined) parts.push(`speed:${formatOverlayValue(params.speed ?? params.pitch)}`);
    if (params.poly !== undefined && params.poly !== 6) parts.push(`poly:${params.poly}`);
    if (params.lifetime && params.lifetime !== "process") parts.push(`life:${params.lifetime}`);
    if (params.release !== undefined) parts.push(`rel:${formatOverlayValue(params.release)}`);
  }

  return parts.join(" | ");
}

/**
 * Create audio overlay - SELF-CONTAINED (does not use createOscOverlay)
 */
// every overlay made, so a project switch takes them all away
const overlays = new Set();

/** Every audio overlay gone (the project changes). */
export function destroyAudioOverlays() {
  for (const o of [...overlays]) o.destroy();
}
onReset("project", destroyAudioOverlays);   // the sounds' labels (waveform:none) and their loops

export function createAudioOverlay({
  anchorEl,
  label = "audio",
  track = true
} = {}) {

  if (!anchorEl) {
    return {
      el: null,
      update() { },
      position() { },
      destroy() { }
    };
  }

  const box = document.createElement("div");
  box.className = "oscilla-audio-overlay";
  box.style.cssText = `
    position: fixed;
    pointer-events: none;
    z-index: 99990;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 11px;
    line-height: 1.3;
    padding: 2px 7px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.2);
    color: #000;
    box-shadow: 0 1px 4px rgba(0,0,0,0.15);
    opacity: 0.75;
    transition: opacity 0.15s ease, background 0.15s ease, border-color 0.15s ease;
  `;

  const r = anchorEl.getBoundingClientRect();
  box.style.maxWidth = `${Math.max(150, r.width - 8)}px`;
  box.style.whiteSpace = "normal";
  box.style.wordWrap = "break-word";

  box.textContent = label;
  document.body.appendChild(box);

  function position() {
    if (!anchorEl || !box.isConnected) return;
    const r = anchorEl.getBoundingClientRect();
    box.style.left = `${r.left + 4}px`;
    box.style.top = `${r.top + 4}px`;
  }

  let tracking = false;
  function loop() {
    if (!tracking) return;
    // its element gone (a page turned, the score reloaded): the label and its loop go too
    if (!anchorEl.isConnected) { overlay.destroy(); return; }
    position();
    requestAnimationFrame(loop);
  }

  if (track) {
    tracking = true;
    requestAnimationFrame(loop);
  }

  position();

  const overlay = {
    el: box,
    update(text) {
      if (text !== undefined) {
        box.textContent = text;
      }
    },
    position,
    destroy() {
      tracking = false;
      box.remove();
      overlays.delete(overlay);
    }
  };
  overlays.add(overlay);
  return overlay;
}
